<script>
	import { onMount, onDestroy } from 'svelte';
	import Chart from 'chart.js/auto';
	import 'chartjs-adapter-date-fns';
	import { createGradient, baseChartOptions, timeXAxis, valueYAxis, darkTooltip, COLORS, registerTimeNearestMode } from './ChartTheme.js';

	export let ourData = [];
	export let samsungData = [];
	export let title = '심박수 비교';

	let chartCanvas;
	let chart;

	function getYRange() {
		let allValues = [];
		if (ourData.length > 0) {
			allValues.push(...ourData.map(d => d.min), ...ourData.map(d => d.max));
		}
		if (samsungData.length > 0) {
			allValues.push(...samsungData.map(d => d.min), ...samsungData.map(d => d.max));
		}
		if (allValues.length === 0) return { min: 40, max: 120 };
		return {
			min: Math.floor(Math.min(...allValues) - 10),
			max: Math.ceil(Math.max(...allValues) + 10),
		};
	}

	function createChart() {
		registerTimeNearestMode();
		if (chart) chart.destroy();
		if (!chartCanvas) return;
		if (ourData.length === 0 && samsungData.length === 0) return;

		const yRange = getYRange();
		const datasets = [];

		if (ourData.length > 0) {
			// Sleepwave 범위 (max-min 영역)
			datasets.push({
				label: 'Sleepwave Range',
				data: ourData.map(d => ({ x: d.time, y: d.max })),
				borderColor: 'transparent',
				backgroundColor: function(context) {
					const { ctx, chartArea } = context.chart;
					return createGradient(ctx, chartArea, '#6366f1', '#6366f1', 0.12, 0.02);
				},
				borderWidth: 0,
				pointRadius: 0,
				fill: '+1',
				order: 3,
			});
			datasets.push({
				label: 'Sleepwave Min',
				data: ourData.map(d => ({ x: d.time, y: d.min })),
				borderColor: 'rgba(99, 102, 241, 0.15)',
				borderWidth: 1,
				pointRadius: 0,
				fill: false,
				order: 3,
			});
			// Sleepwave 평균선
			datasets.push({
				label: 'Sleepwave',
				data: ourData.map(d => ({ x: d.time, y: d.avg })),
				borderColor: '#6366f1',
				backgroundColor: 'transparent',
				borderWidth: 2,
				pointRadius: 0,
				pointHoverRadius: 4,
				pointHoverBackgroundColor: '#6366f1',
				pointHoverBorderColor: '#fff',
				pointHoverBorderWidth: 2,
				fill: false,
				tension: 0.2,
				order: 1,
			});
		}

		if (samsungData.length > 0) {
			// Samsung 범위
			datasets.push({
				label: 'Samsung Range',
				data: samsungData.map(d => ({ x: d.startTime, y: d.max })),
				borderColor: 'transparent',
				backgroundColor: 'rgba(244, 114, 182, 0.06)',
				borderWidth: 0,
				pointRadius: 0,
				fill: '+1',
				order: 4,
			});
			datasets.push({
				label: 'Samsung Min',
				data: samsungData.map(d => ({ x: d.startTime, y: d.min })),
				borderColor: 'rgba(244, 114, 182, 0.1)',
				borderWidth: 1,
				pointRadius: 0,
				fill: false,
				order: 4,
			});
			// Samsung 평균선
			datasets.push({
				label: 'Samsung',
				data: samsungData.map(d => ({ x: d.startTime, y: d.heartRate })),
				borderColor: '#f472b6',
				backgroundColor: 'transparent',
				borderWidth: 1.5,
				pointRadius: 0,
				pointHoverRadius: 4,
				pointHoverBackgroundColor: '#f472b6',
				pointHoverBorderColor: '#fff',
				pointHoverBorderWidth: 2,
				fill: false,
				borderDash: [6, 4],
				tension: 0.2,
				order: 2,
			});
		}

		chart = new Chart(chartCanvas, {
			type: 'line',
			data: { datasets },
			options: {
				...baseChartOptions(),
				plugins: {
					legend: {
						display: datasets.length > 1,
						position: 'top',
						align: 'end',
						labels: {
							color: COLORS.textSecondary,
							font: { size: 11 },
							boxWidth: 12,
							boxHeight: 8,
							padding: 16,
							borderRadius: 4,
							filter: item => !item.text.includes('Range') && !item.text.includes('Min'),
						},
						onClick: function(e, legendItem, legend) {
							const source = legendItem.text.toLowerCase();
							const ci = legend.chart;
							ci.data.datasets.forEach((ds, i) => {
								if (ds.label.toLowerCase().startsWith(source)) {
									ci.setDatasetVisibility(i, !ci.isDatasetVisible(i));
								}
							});
							ci.update();
						}
					},
					tooltip: {
						...darkTooltip,
						filter: item => !item.dataset.label.includes('Range') && !item.dataset.label.includes('Min'),
						callbacks: {
							label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y} bpm`,
							title: items => {
								if (items.length > 0) {
									return new Date(items[0].parsed.x).toLocaleTimeString('ko-KR', {
										hour: '2-digit', minute: '2-digit'
									});
								}
								return '';
							},
							labelColor: function(ctx) {
								return {
									borderColor: ctx.dataset.borderColor,
									backgroundColor: ctx.dataset.borderColor,
									borderWidth: 2,
									borderRadius: 2,
								};
							}
						}
					}
				},
				scales: {
					x: timeXAxis(),
					y: valueYAxis({
						min: yRange.min,
						max: yRange.max,
						ticks: {
							color: COLORS.textSecondary,
							font: { size: 11 },
							padding: 6,
							callback: val => `${val}`,
						},
						grid: {
							color: COLORS.gridLine,
							drawBorder: false,
							tickLength: 0,
						},
						title: { display: true, text: 'BPM', color: COLORS.textMuted, font: { size: 11 } },
					}),
				}
			}
		});
	}

	$: if (chartCanvas && (ourData || samsungData)) {
		createChart();
	}

	onMount(() => {
		if (ourData.length > 0 || samsungData.length > 0) {
			createChart();
		}
	});

	onDestroy(() => {
		if (chart) chart.destroy();
	});
</script>

<div class="chart-card">
	<div class="chart-header">
		<h5 class="chart-title">{title}</h5>
	</div>
	<div class="chart-body">
		{#if ourData.length === 0 && samsungData.length === 0}
			<div class="empty-state">심박수 데이터가 없습니다</div>
		{:else}
			<div class="chart-container">
				<canvas bind:this={chartCanvas}></canvas>
			</div>
		{/if}
	</div>
</div>

<style>
	.chart-card {
		background: #ffffff;
		border: 1px solid #e2e8f0;
		border-radius: 16px;
		padding: 20px 24px 16px;
		margin-bottom: 20px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
	}
	.chart-header {
		margin-bottom: 12px;
	}
	.chart-title {
		font-size: 15px;
		font-weight: 600;
		color: #334155;
		margin: 0;
	}
	.chart-body {
		position: relative;
	}
	.chart-container {
		height: 220px;
	}
	.empty-state {
		text-align: center;
		color: #94a3b8;
		padding: 48px 0;
		font-size: 14px;
	}
</style>
